import { Tab, TabGroup, TabList, TabPanel, TabPanels, Transition } from '@headlessui/react'
import classNames from 'classnames'
import React, { Fragment } from 'react'

import styles from './Tabs.module.scss'

type TabItem = {
  key?: string
  label: React.ReactNode
  content?: React.ReactNode
  disabled?: boolean
}

type TabsProps = {
  items: Readonly<TabItem[]>
  defaultIndex?: number
  selectedIndex?: number
  onChange?: (index: number) => void
}

export default function Tabs({ items, defaultIndex, selectedIndex, onChange }: TabsProps) {
  return (
    <TabGroup
      className={styles['tab-group']}
      defaultIndex={defaultIndex}
      selectedIndex={selectedIndex}
      onChange={onChange}
    >
      <TabList className={styles['tab-list']}>
        <div className={styles['active-tab-indicator-container']}>
          <div
            style={{
              transform: `translateX(calc(${selectedIndex} * 100%))`,
              transition: 'transform 200ms ease',
              width: `calc(100% / ${items.length})`,
              height: '100%',
              borderRadius: '999px',
              backgroundColor: 'var(--vip-brand-bttn-color-secondary-brand-enabled)',
            }}
          />
        </div>
        {items.map((item, i, arr) => {
          const isAbleToHaveADivider = arr.length > 2 && i !== 0 && i !== arr.length - 1 && selectedIndex !== i

          return (
            <Tab
              key={item.key ?? i}
              disabled={item.disabled}
              className={({ selected }) => classNames(styles.tab, { [styles.active]: selected })}
            >
              {item.label}

              {isAbleToHaveADivider && (
                <div
                  className={classNames(styles.divider, {
                    [styles.after]: selectedIndex === 0,
                    [styles.before]: selectedIndex === arr.length - 1,
                  })}
                />
              )}
            </Tab>
          )
        })}
      </TabList>

      <TabPanels>
        {items.map((item, i) => {
          if (!item.content) {
            return null
          }

          return (
            <TabPanel unmount={false} key={item.key ?? i}>
              <Transition
                as={Fragment}
                appear
                show={selectedIndex === i}
                enter={styles.tabPanelEnter}
                enterFrom={styles.tabPanelEnterFrom}
                enterTo={styles.tabPanelEnterTo}
              >
                <div>{item.content}</div>
              </Transition>
            </TabPanel>
          )
        })}
      </TabPanels>
    </TabGroup>
  )
}
